import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SimplizeLogo from './SimplizeLogo';
import LoginModal from './LoginModal';
import { Storage } from '../constants/storage';

const HomeDemo = ({ onLoginSuccess }) => {
    const navigate = useNavigate();
    const [isLoginOpen, setIsLoginOpen] = useState(false);
    const [marketData, setMarketData] = useState([]);
    const [vnindexData, setVnindexData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const CACHE_KEY = Storage.market_data;

        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
            try {
                const data = JSON.parse(cached);
                setMarketData(data);
                setLoading(false);
            } catch (error) {
                console.error('Error parsing cached data:', error);
            }
        }

        const fetchMarketData = async () => {
            try {
                const response = await fetch('https://api2.simplize.vn/api/company/se/market/tickers?tickers=VNINDEX,%5EDJI,%5EN225,GC%3DF,CL%3DF,BTCUSDT');
                const result = await response.json();

                if (result.status === 200 && result.data) {
                    const vnindex = result.data.find(item => item.ticker === 'VNINDEX');
                    const others = result.data.filter(item => item.ticker !== 'VNINDEX');

                    const shuffled = others.sort(() => 0.5 - Math.random());
                    const selected = shuffled.slice(0, 2);

                    const newData = vnindex ? [vnindex, ...selected] : selected.slice(0, 3);
                    setMarketData(newData);
                    localStorage.setItem(CACHE_KEY, JSON.stringify(newData));
                }
            } catch (error) {
                console.error('Error fetching market data:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchMarketData();
    }, []);

    useEffect(() => {
        const VNINDEX_CACHE_KEY = Storage.vnindex_data;

        const cached = localStorage.getItem(VNINDEX_CACHE_KEY);
        if (cached) {
            try {
                const data = JSON.parse(cached);
                setVnindexData(data);
            } catch (error) {
                console.error('Error parsing cached VNINDEX data:', error);
            }
        }

        const fetchVnindexData = async () => {
            try {
                const response = await fetch('https://api2.simplize.vn/api/historical/quote/VNINDEX?type=index');
                const result = await response.json();

                if (result.status === 200 && result.data) {
                    setVnindexData(result.data);
                    localStorage.setItem(VNINDEX_CACHE_KEY, JSON.stringify(result.data));
                }
            } catch (error) {
                console.error('Error fetching VNINDEX data:', error);
            }
        };

        fetchVnindexData();
    }, []);

    const formatNumber = (num) => {
        if (!num) return '0';
        return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatPercent = (num) => {
        if (!num) return '0.00';
        const formatted = Math.abs(num).toFixed(2);
        return num >= 0 ? `+${formatted}` : `-${formatted}`;
    };

    const getColorClass = (num) => {
        return num >= 0 ? 'text-green-600' : 'text-red-600';
    };

    const formatVolume = (vol) => {
        if (!vol) return '-';
        if (vol >= 1e9) return `${(vol / 1e9).toFixed(1)}B`;
        if (vol >= 1e6) return `${(vol / 1e6).toFixed(0)}M`;
        if (vol >= 1e3) return `${(vol / 1e3).toFixed(0)}K`;
        return vol.toString();
    };

    const formatLiquidity = (value) => {
        if (!value) return '-';
        const trillion = value / 1e12;
        if (trillion >= 1000) return `${(trillion / 1000).toFixed(1)}M tỷ`;
        if (trillion >= 1) return `${(trillion).toFixed(1)}K tỷ`;
        return `${(trillion * 1000).toFixed(0)} tỷ`;
    };

    const formatVolumeCP = (vol) => {
        if (!vol) return '-';
        if (vol >= 1e9) return `${(vol / 1e9).toFixed(1)}B CP`;
        if (vol >= 1e6) return `${(vol / 1e6).toFixed(0)}M CP`;
        if (vol >= 1e3) return `${(vol / 1e3).toFixed(0)}K CP`;
        return `${vol} CP`;
    };

    const handleLoginSuccess = () => {
        setIsLoginOpen(false);
        if (onLoginSuccess) {
            onLoginSuccess();
        }
        navigate('/');
    };

    return (
        <div className="min-h-screen bg-white relative">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20" style={{ paddingBottom: 'max(5rem, env(safe-area-inset-bottom))' }}>
                {/* Centered Logo at top of page */}
                <div className="flex justify-center mb-5 opacity-0 animate-fadeIn" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
                    <SimplizeLogo className="h-8 sm:h-10 md:h-12 hover:opacity-80 transition-opacity cursor-pointer" />
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-12 lg:gap-16 lg:items-center">
                    {/* Left Content */}
                    <div className="space-y-6 sm:space-y-8 animate-fadeIn">
                        {/* Eyebrow */}
                        <div className="inline-flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-orange-50 rounded-full">
                            <div className="w-2 h-2 bg-brand-500 rounded-full animate-pulse"></div>
                            <span className="text-xs sm:text-sm font-medium text-brand-600">Trợ lý AI chứng khoán thông minh</span>
                        </div>

                        {/* Main Heading */}
                        <div>
                            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-8 sm:mb-16">
                                Không thể?
                            </h1>
                            <p className="text-lg sm:text-xl md:text-2xl text-gray-600 leading-relaxed">
                                Đầu tư chứng khoán thông minh với AI. Phân tích sâu, định giá chính xác, quyết định tự tin.
                            </p>
                        </div>

                        {/* Features List */}
                        <div className="space-y-3 sm:space-y-4">
                            <div className="flex items-start gap-2.5 sm:gap-3">
                                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Phân tích thị trường real-time</h3>
                                    <p className="text-gray-600 text-xs sm:text-sm">Cập nhật VN-Index, HNX và hơn 1.000 mã cổ phiếu</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-2.5 sm:gap-3">
                                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">Định giá chuyên sâu</h3>
                                    <p className="text-gray-600 text-xs sm:text-sm">Phân tích P/E, P/B, ROE và hơn 20 chỉ số tài chính</p>
                                </div>
                            </div>

                            <div className="flex items-start gap-2.5 sm:gap-3">
                                <div className="w-5 h-5 sm:w-6 sm:h-6 bg-brand-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                    <svg className="w-3 h-3 sm:w-4 sm:h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-900 text-sm sm:text-base">AI hiểu ngữ cảnh</h3>
                                    <p className="text-gray-600 text-xs sm:text-sm">Trả lời câu hỏi phức tạp, đưa ra gợi ý đầu tư</p>
                                </div>
                            </div>
                        </div>

                        {/* CTA Button */}
                        <div className="pt-2 sm:pt-4">
                            <button
                                onClick={() => setIsLoginOpen(true)}
                                className="group relative inline-flex w-full sm:w-auto items-center justify-center gap-2 sm:gap-3 bg-gray-900 hover:bg-gray-800 active:bg-gray-700 text-white font-semibold px-6 sm:px-8 py-3 sm:py-4 rounded-xl sm:rounded-2xl transition-all duration-200 shadow-xl hover:shadow-2xl sm:hover:scale-105 active:scale-95"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                                <SimplizeLogo iconOnly={true} className="h-5 w-5 sm:h-6 sm:w-6" />
                                <span className="text-sm sm:text-base">Đăng nhập để sử dụng ngay</span>
                                <svg className="w-4 h-4 sm:w-5 sm:h-5 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                </svg>
                            </button>
                        </div>
                    </div>

                    {/* Right Visual */}
                    <div className="relative lg:h-[600px] h-[400px] sm:h-[500px] flex items-center justify-center animate-fadeIn mt-8 lg:mt-0" style={{ animationDelay: '0.2s' }}>
                        {/* Background Gradient Blobs */}
                        <div className="absolute inset-0 overflow-hidden">
                            <div className="absolute top-1/4 -right-10 w-48 h-48 sm:w-64 sm:h-64 lg:w-72 lg:h-72 bg-gradient-to-br from-brand-400 to-brand-600 rounded-full blur-3xl opacity-20 animate-pulse"></div>
                            <div className="absolute bottom-1/4 -left-10 w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96 bg-gradient-to-tr from-orange-300 to-brand-500 rounded-full blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }}></div>
                        </div>

                        {/* Main Visual Container */}
                        <div className="relative w-full max-w-md px-2 sm:px-0">
                            {/* Floating Cards */}
                            <div className="space-y-3 sm:space-y-4">
                                {/* Card 1 - Market Tickers */}
                                <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl p-3 sm:p-4 border border-gray-100 transform hover:scale-105 transition-transform duration-300 opacity-0 h-[160px] sm:h-[180px]" style={{ animation: 'cardSlideIn 0.6s ease-out 0.3s forwards, cardFloat 6s ease-in-out 1.2s infinite' }}>
                                    <div className="space-y-2 sm:space-y-2.5 h-full flex flex-col justify-between">
                                        {[0, 1, 2].map((index) => {
                                            const ticker = marketData[index];
                                            return ticker ? (
                                                <div
                                                    key={ticker.ticker || index}
                                                    className={`flex items-center justify-between ${index < 2 ? 'pb-2 sm:pb-2.5 border-b border-gray-100' : ''}`}
                                                >
                                                    <div className="flex items-center gap-1.5 sm:gap-2 flex-1 min-w-0">
                                                        {ticker.imageUrl && (
                                                            <img
                                                                src={ticker.imageUrl}
                                                                alt={ticker.name}
                                                                className="w-5 h-5 sm:w-6 sm:h-6 rounded-full object-cover flex-shrink-0"
                                                            />
                                                        )}
                                                        <div className="min-w-0 flex-1">
                                                            <div className="text-[11px] sm:text-xs font-medium text-gray-900 truncate">{ticker.name}</div>
                                                            <div className="text-xs sm:text-sm font-bold text-gray-900">{formatNumber(ticker.indexClose)}</div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0 ml-2">
                                                        <div className={`text-[11px] sm:text-xs font-semibold ${getColorClass(ticker.pctChange)}`}>
                                                            {formatPercent(ticker.pctChange)}%
                                                        </div>
                                                        <div className={`text-[10px] ${getColorClass(ticker.netChange)}`}>
                                                            {ticker.netChange >= 0 ? '+' : ''}{formatNumber(ticker.netChange)}
                                                        </div>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div
                                                    key={index}
                                                    className={`flex items-center justify-between animate-pulse ${index < 2 ? 'pb-2 sm:pb-2.5 border-b border-gray-100' : ''}`}
                                                >
                                                    <div className="flex items-center gap-1.5 sm:gap-2 flex-1">
                                                        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-gray-200 rounded-full flex-shrink-0"></div>
                                                        <div className="flex-1">
                                                            <div className="h-2.5 sm:h-3 bg-gray-200 rounded w-20 sm:w-24 mb-1.5 sm:mb-2"></div>
                                                            <div className="h-3 sm:h-3.5 bg-gray-200 rounded w-16 sm:w-20"></div>
                                                        </div>
                                                    </div>
                                                    <div className="text-right flex-shrink-0 ml-2">
                                                        <div className="h-2.5 sm:h-3 bg-gray-200 rounded w-10 sm:w-12 mb-1"></div>
                                                        <div className="h-2.5 bg-gray-200 rounded w-8 sm:w-10"></div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* Card 2 - AI Analysis */}
                                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 text-white transform hover:scale-105 transition-transform duration-300 ml-4 sm:ml-8 opacity-0" style={{ animation: 'cardSlideIn 0.6s ease-out 0.5s forwards, cardFloat 6s ease-in-out 1.6s infinite' }}>
                                    <div className="flex items-start gap-2 sm:gap-3 mb-2 sm:mb-3">
                                        <div className="w-7 h-7 sm:w-8 sm:h-8 bg-brand-500 rounded-full flex items-center justify-center flex-shrink-0">
                                            <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                            </svg>
                                        </div>
                                        <div className="flex-1">
                                            <div className="text-[10px] sm:text-xs font-medium text-gray-400 mb-1">AI Phân tích</div>
                                            <div className="text-xs sm:text-sm leading-relaxed">
                                                Cổ phiếu VNM đang có xu hướng tích cực với P/E 18.5x, thấp hơn trung bình ngành...
                                            </div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2 pt-2 border-t border-gray-700">
                                        <div className="flex-1 text-[10px] sm:text-xs text-gray-400">Gợi ý</div>
                                        <div className="px-2 sm:px-3 py-0.5 sm:py-1 bg-green-600 rounded-full text-[10px] sm:text-xs font-semibold">MUA</div>
                                    </div>
                                </div>

                                {/* Card 3 - Stats */}
                                <div className="bg-white rounded-xl sm:rounded-2xl shadow-2xl p-4 sm:p-6 border border-gray-100 transform hover:scale-105 transition-transform duration-300 opacity-0" style={{ animation: 'cardSlideIn 0.6s ease-out 0.7s forwards, cardFloat 6s ease-in-out 2s infinite' }}>
                                    <div className="grid grid-cols-2 gap-3 sm:gap-4">
                                        <div>
                                            <div className="text-[10px] sm:text-xs text-gray-500 mb-1">Thanh khoản</div>
                                            <div className="text-base sm:text-lg font-bold text-gray-900">
                                                {vnindexData ? formatLiquidity(vnindexData.totalValue) : '18.5K tỷ'}
                                            </div>
                                        </div>
                                        <div>
                                            <div className="text-[10px] sm:text-xs text-gray-500 mb-1">Khối lượng</div>
                                            <div className="text-base sm:text-lg font-bold text-gray-900">
                                                {vnindexData ? formatVolumeCP(vnindexData.totalVolume) : '850M CP'}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add keyframe animations */}
            <style jsx>{`
                @keyframes cardSlideIn {
                    from {
                        opacity: 0;
                        transform: translateY(30px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                @keyframes cardFloat {
                    0%, 100% {
                        transform: translateY(0px);
                    }
                    50% {
                        transform: translateY(-20px);
                    }
                }
                @keyframes grow {
                    from { transform: scaleY(0); }
                    to { transform: scaleY(1); }
                }
            `}</style>

            {/* Login Modal */}
            <LoginModal
                isOpen={isLoginOpen}
                onClose={() => setIsLoginOpen(false)}
                onSuccess={handleLoginSuccess}
            />
        </div>
    );
};

export default HomeDemo;
